import { createContext, FC, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { has, isEmpty, isNil, keyBy, noop } from 'lodash';
import { isNode, type Node } from 'reactflow';
// Import Hooks
import useRequest from 'hooks/UseRequest';
import useCancelableRequests from 'hooks/UseCancelableRequests';
// Import Services
import { CustomerJourneyHttpService } from 'services/http';
import { getBlockType } from 'services/Helpers';
import {
    getDependencies,
    groupBlocksBuilder,
    mapConfigToFlowEditorModel,
    propsCollector,
    setAutomapOptionsValues,
} from 'services/customerJourney/helpers';
// Import Types
import type { CJBudgetGroupApiModel } from 'types';
// Import Constants
import { JSON_OBJECT, l, RealtimePromotionTypes } from 'constants/common';
// Import Enums
import { PageTypesEnum } from 'enums';

export interface WorkflowConfigBlock {
    Name: string;
    DisplayName: string;
    TypeName?: string;
    [key: string]: unknown;
}

export interface WorkflowBlockDisplayInfo {
    isRemoved: boolean;
    displayName: string;
    typeLabelKey: string;
}

export interface WorkflowBlockConfiguration {
    blockId: string;
    options: AnyObject[];
    apiModel: AnyObject;
    elements: Node[];
}

interface CJWorkflowBlocksContextValue {
    blocksInfoMap: Record<string, WorkflowConfigBlock>;
    isLoading: boolean;
    ensureLoaded: () => void;
    getBlockInfo: (blockName: string) => WorkflowBlockDisplayInfo;
    ensureSchemaLoaded: () => void;
    isSchemaLoading: boolean;
    getBlockConfiguration: (blockName: string) => Nullable<WorkflowBlockConfiguration>;
    budgets: AnyObject[];
    blockElements: Node[];
}

const CJWorkflowBlocksContext = createContext<CJWorkflowBlocksContextValue>({
    blocksInfoMap: {},
    isLoading: false,
    ensureLoaded: noop,
    getBlockInfo: (blockName) => ({ isRemoved: false, displayName: blockName, typeLabelKey: '' }),
    ensureSchemaLoaded: noop,
    isSchemaLoading: false,
    getBlockConfiguration: () => null,
    budgets: [],
    blockElements: [],
});

export const CJWorkflowBlocksProvider: FC<{ workflowId?: string }> = ({ workflowId, children }) => {
    const { doGetRequest } = useRequest();

    const [blocksInfoMap, setBlocksInfoMap] = useState<Record<string, WorkflowConfigBlock>>({});
    const [configBlocks, setConfigBlocks] = useState<AnyObject[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [isLoaded, setIsLoaded] = useState(false);
    const [budgets, setBudgets] = useState<CJBudgetGroupApiModel[]>([]);
    const hasRequestedRef = useRef(false);

    const [groupBlocks, setGroupBlocks] = useState<AnyObject[]>([]);
    const [blockElements, setBlockElements] = useState<Node[]>([]);
    const [isSchemaRequested, setIsSchemaRequested] = useState(false);
    const [isSchemaLoading, setIsSchemaLoading] = useState(false);
    const hasRequestedSchemaRef = useRef(false);

    const { getCustomerJourneyPreviewByIdRequest, getCustomerJourneySchemaRequest } = useCancelableRequests({
        getCustomerJourneyPreviewByIdRequest: CustomerJourneyHttpService.getCustomerJourneyPreviewById,
        getCustomerJourneySchemaRequest: CustomerJourneyHttpService.getCustomerJourneySchema,
    });

    const ensureLoaded = useCallback(() => {
        if (hasRequestedRef.current || isNil(workflowId)) {
            return;
        }
        hasRequestedRef.current = true;

        setIsLoading(true);
        doGetRequest(getCustomerJourneyPreviewByIdRequest.request, {
            queryString: { workflowId },
            successCallback: (data) => {
                const blocks: WorkflowConfigBlock[] = JSON.parse(data?.Config ?? JSON_OBJECT)?.Blocks ?? [];
                setConfigBlocks(blocks);
                setBlocksInfoMap(keyBy(blocks, 'Name'));
                setBudgets(data?.Budgets ?? []);
                setIsLoaded(true);
            },
        }).then(() => {
            setIsLoading(false);
        });
    }, [workflowId, doGetRequest, getCustomerJourneyPreviewByIdRequest]);

    const getBlockInfo = useCallback(
        (blockName: string): WorkflowBlockDisplayInfo => {
            const block = blocksInfoMap[blockName];
            if (block) {
                const typeLabelKey = `CJBlock${getBlockType(block.TypeName)}`;
                return {
                    isRemoved: false,
                    displayName: block.DisplayName,
                    typeLabelKey: has(l, typeLabelKey) ? typeLabelKey : '',
                };
            }
            return { isRemoved: isLoaded, displayName: blockName, typeLabelKey: '' };
        },
        [blocksInfoMap, isLoaded],
    );

    const ensureSchemaLoaded = useCallback(() => {
        ensureLoaded();
        setIsSchemaRequested(true);
    }, [ensureLoaded]);

    useEffect(() => {
        if (!isSchemaRequested || hasRequestedSchemaRef.current) {
            return;
        }
        hasRequestedSchemaRef.current = true;

        setIsSchemaLoading(true);
        doGetRequest(getCustomerJourneySchemaRequest.request, {
            queryString: { promotionType: RealtimePromotionTypes.CustomerJourney },
            successCallback: (data) => {
                setGroupBlocks(groupBlocksBuilder(data));
            },
        }).then(() => {
            // Clear the loader on request completion (success, empty schema, or failure alike);
            // an empty/failed result leaves groupBlocks empty, so the mapping effect below never runs.
            setIsSchemaLoading(false);
        });
    }, [isSchemaRequested, doGetRequest, getCustomerJourneySchemaRequest]);

    useEffect(() => {
        if (isEmpty(groupBlocks) || isEmpty(configBlocks)) {
            return;
        }

        let isCancelled = false;
        const elements = mapConfigToFlowEditorModel(configBlocks, groupBlocks) as Node[];

        // getDependencies is async: it fetches dependency data (bonuses, providers, etc.)
        // and only attaches node.data.dependencies once those requests resolve.
        // Publish blockElements after it settles so consumers see the dependencies.
        getDependencies(elements, PageTypesEnum.edit).finally(() => {
            if (isCancelled) {
                return;
            }
            elements.forEach((element) => {
                if (isNode(element)) {
                    element.data.collectedProperties = propsCollector(element, elements);
                    setAutomapOptionsValues(element, elements);
                }
            });
            setBlockElements([...elements]);
        });

        return () => {
            isCancelled = true;
        };
    }, [groupBlocks, configBlocks]);

    const getBlockConfiguration = useCallback(
        (blockName: string): Nullable<WorkflowBlockConfiguration> => {
            const element = blockElements.find((el) => el.id === blockName);
            if (isNil(element)) {
                return null;
            }
            return {
                blockId: element.id,
                options: element.data?.metaData?.Options ?? [],
                apiModel: element.data?.apiModel ?? {},
                elements: blockElements,
            };
        },
        [blockElements],
    );

    const value = useMemo(
        () => ({
            blocksInfoMap,
            isLoading,
            ensureLoaded,
            getBlockInfo,
            ensureSchemaLoaded,
            isSchemaLoading,
            getBlockConfiguration,
            budgets,
            blockElements,
        }),
        [
            blocksInfoMap,
            isLoading,
            ensureLoaded,
            getBlockInfo,
            ensureSchemaLoaded,
            isSchemaLoading,
            getBlockConfiguration,
            budgets,
            blockElements,
        ],
    );

    return <CJWorkflowBlocksContext.Provider value={value}>{children}</CJWorkflowBlocksContext.Provider>;
};

export const useCJWorkflowBlocks = () => useContext(CJWorkflowBlocksContext);
